import { Key } from '~/utils/key';

/**
 * Aurelia Store semver:
 * - major: breaking change and state needs a reset
 * - minor: additional entry that's typically optional
 */
export const STORE_VERSION = '9.0';
/**
 * History size
 */
export const STORE_HISTORY_LIMIT: number = 1;
/**
 * Recent entity search limit
 */
export const ENTITY_SEARCH_HISTORY_LIMIT: number = 5;
/**
 * Navigation history limit
 */
export const NAVIGATION_HISTORY_LIMIT: number = 20;
/**
 * Standard animation duration in ms
 */
export const ANIMATION_DURATION: number = 150;
/**
 * Standard throttling duration in ms
 */
export const THROTTLE_DURATION: number = 200;
/**
 * Standard debounce duration in ms
 */
export const DEBOUNCE_DURATION: number = 200;
/**
 * Debounce duration for selecting a record in a DataTable.
 *
 * This is larger than the default to give enough time for
 * clicking with the mouse, moving the mouse and clicking again.
 */
export const DEBOUNCE_RECORD_SELECT_DURATION: number = 600;
/**
 * Get all records with acurity table
 */
export const TABLE_GET_ALL_RECORDS: number = -1;
/**
 * The name of the session variable that Acurity sets to "1" when WEBUI loads within TreeView.
 * This should be kept the same as au.com.finsyn.util.jxbrowser.JxBrowserUtils.TREE_VIEW_SESSION_VARIABLE
 */
export const TREE_VIEW_SESSION_VARIABLE_NAME: string = 'is_tree_view';
/**
 * App Events
 */
export const GlobalEvents = {
	Scroll: {
		Top: 'Scroll.Top',
	},
};

/**
 * Parameters in front-end routing. Usually to be processed by the front-end to prefill forms, pre-apply filters, etc
 */
export enum CommonURLParam {
	BATCHES = 'batches',
	BATCH_ID = 'batch-id',
	BATCH_REF = 'batch-ref',
	BATCH_ENV = 'batch-env',
	FUND = 'fund',
	EMPLOYER = 'employer',
	MEMBER = 'member',
	CATEGORY = 'category',
	CLIENT = 'client',
	INVESTMENT_MANAGER = 'investment-manager',
	ADVISER = 'adviser',
	TRANS_REF = 'transaction-ref',
	TRANSACTION_ID = 'transaction-id',
	TRANSACTION_TYPE = 'transaction-type',
	RECORD_ID = 'record-id',
	RECEIPT_REF = 'receipt-ref',
	FILE_REG_REF = 'file-reg-ref',
	LINE_NO = 'line-no',
	DIVISION = 'division',
	NAV_VIEW = 'nav-view',
	NAV_ID = 'nav-id',
	NAV_HIDE = 'nav-hide',
	CUSTOMISATION = 'customisation',
	SERVER_URL = 'server-url',
	IDS = 'ids',
	TABLE_CODE = 'table-code',
	REDIRECT_FROM = 'redirectFrom',
	SSO = 'sso',
	SHARE_CERTIFICATE_NUMBER_SYSTEM = 'share-cert-no-sys',
	API_AUDIT_SESSION_TOKEN = 'api-audit-session-token',
	API_CORRELATION_ID = 'api-correlation-id',
	RETENTION_PROCESSING_ID = 'retention-processing-id',
	/*
	 * Case360 screen pop pre-display data fields
	 */
	CUSTOM_DATA = 'customData',
	ENTITY_TYPE = 'entityType',
	ENTITY_ID = 'entityId',
	CASE_ID = 'caseId',
	IDENTITY = 'identity',
	ISSUE = 'issue',
	USE_ARCHIVE = 'useArchive',
}

interface ShortcutKeyMap {
	// Page
	View: readonly Key[];
	Edit: readonly Key[];
	Export: readonly Key[];
	Delete: readonly Key[];
	Notes: readonly Key[];
	Audit: readonly Key[];
	Save: readonly Key[];
	Close: readonly Key[];
	CloneSelected: readonly Key[];
	AddRecord: readonly Key[];

	// Filter
	ToggleFilter: readonly Key[];
	ClearFilter: readonly Key[];

	// AcurityTable
	PrevRecord: readonly Key[];
	NextRecord: readonly Key[];
	PrevPage: readonly Key[];
	NextPage: readonly Key[];
	PrevPageAlt: readonly Key[];
	NextPageAlt: readonly Key[];
	FirstPage: readonly Key[];
	LastPage: readonly Key[];
	FirstPageAlt: readonly Key[];
	LastPageAlt: readonly Key[];
	ReverseOrder: readonly Key[];

	// Navigation
	JumpToNavFilter: readonly Key[];

	// Form
	FocusFirstField: readonly Key[];
	FocusLastField: readonly Key[];

	// Misc
	CustomisationEditor: readonly Key[];
	HelpFile: readonly Key[];
	ProcedureFile: readonly Key[];
}

/**
 * ShortcutKey maps a behaviour to a set of keys.
 * This exists to keep key configurations consistent across forms.
 *
 * References:
 * - Supported shortcut keys
 *     - https://service.financialsynergy.com.au/pages/viewpage.action?pageId=40109606
 */
export const ShortcutKey: Readonly<ShortcutKeyMap> = {
	// Page
	View: [Key.ENTER],
	Edit: [Key.CONTROL, Key.SHIFT, Key.E],
	Export: [Key.CONTROL, Key.SHIFT, Key.S],
	Delete: [Key.ALT, Key.DELETE],
	Notes: [Key.F3],
	Audit: [Key.SHIFT, Key.F3],
	Save: [Key.F10],
	Close: [Key.ESCAPE],
	CloneSelected: [Key.CONTROL, Key.SHIFT, Key.L],
	AddRecord: [Key.CONTROL, Key.SHIFT, Key.A],

	// Filter
	ToggleFilter: [Key.CONTROL, Key.SHIFT, Key.F],
	ClearFilter: [Key.ALT, Key.BACKSPACE],

	// AcurityTable
	PrevRecord: [Key.F7],
	NextRecord: [Key.F8],
	PrevPage: [Key.CONTROL, Key.F7],
	NextPage: [Key.CONTROL, Key.F8],
	PrevPageAlt: [Key.PAGE_UP],
	NextPageAlt: [Key.PAGE_DOWN],
	FirstPage: [Key.CONTROL, Key.SHIFT, Key.F7],
	LastPage: [Key.CONTROL, Key.SHIFT, Key.F8],
	FirstPageAlt: [Key.HOME],
	LastPageAlt: [Key.END],
	ReverseOrder: [Key.CONTROL, Key.SHIFT, Key.R],

	// Navigation
	JumpToNavFilter: [Key.CONTROL, Key.SHIFT, Key.K],

	// Form
	FocusFirstField: [Key.CONTROL, Key.HOME],
	FocusLastField: [Key.CONTROL, Key.END],

	// Misc
	CustomisationEditor: [Key.SHIFT, Key.F11],
	HelpFile: [Key.F1],
	ProcedureFile: [Key.ALT, Key.r],
};
